import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import { ERPGIcon } from "motw_core/UI/Icons/ERPGIcon";
import * as toastr from "toastr";
import * as React from "react";
import { SaveNodeAsync } from "source/app/presentation/MainPanel/Workflows/SearchNodes/Helpers/SaveNodeAsync";
import { SearchNodesLink } from "source/app/presentation/MainPanel/Workflows/SearchNodes/Link/SearchNodesLink";
import { ExecuteSearchAsync } from "source/app/presentation/MainPanel/Workflows/SearchNodes/Update/ExecuteSearch";
import { FormTextArea } from "source/app/presentation/Shared/Form/TextArea/FormTextArea";
import { Sounds } from "../../../../../services/Sound/Sounds";
import { DisplayToastrError } from "../../../../Shared/HandleError";
import {
    todayNodeName,
    workflowRepository,
} from "motw_core/Repositories/WorkflowRepository";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import { WithAnimatedProgressBarAsync } from "source/app/presentation/Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import Render from "source/app/presentation/Shared/Render";
import { LoadingSpinner } from "../../../Calendar/LoadingSpinner";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";
import { SearchNodes } from "../SearchNodes";
import { GetCleanText } from "./GetCleanText";

export function SearchBox({
    SearchNodes,
    ClearSearch,
    rootNode,
    canAddNode,
}: {
    SearchNodes: SearchNodes;
    ClearSearch: () => void;
    rootNode: IWorkflowNode;
    canAddNode: boolean;
}) {
    const searchText = SearchNodes.state.searchText;

    return (
        <div>
            <FormTextArea
                id="calcTarget_searchNodes"
                iconSuffix={ERPGIcon["gamepad-cross"]}
                onChange={(searchText) => {
                    const cleanText = GetCleanText(searchText);

                    SearchNodes.setState({
                        searchText: cleanText,
                        showFavorites: false,
                    });
                }}
                onClick={ClearSearch}
                onClear={ClearSearch}
                onKeyDown={async (event) => {
                    // Ctrl + ` to search
                    if (event.ctrlKey && event.keyCode === 192) {
                        await ExecuteSearchAsync();
                    }
                }}
                onEnter={async () => {
                    try {
                        SearchNodes.setState({ showLoadingIndicator: true });

                        await HandleOnEnterAsync(
                            searchText,
                            rootNode,
                            canAddNode
                        );
                    } finally {
                        SearchNodes.setState({ showLoadingIndicator: false });
                    }
                }}
                width="auto"
                value={searchText}
                placeholder={
                    rootNode.name === todayNodeName
                        ? `one small step...`
                        : "Search / Add"
                }
            />
            <SearchNodesLink searchText={searchText} />
            <Render
                id="searchBoxLoadingIndicator"
                when={SearchNodes.state.showLoadingIndicator}
            >
                <LoadingSpinner
                    className={StyleClassAuto({
                        marginLeft: `5px`,
                    })}
                />
            </Render>
        </div>
    );
}

async function HandleOnEnterAsync(
    searchText: string,
    rootNode: IWorkflowNode,
    canAddNode: boolean
) {
    const parentNode = await WithAnimatedProgressBarAsync(() =>
        workflowRepository.GetParentNodeAsync(ClientDataProvider, rootNode)
    );

    var isGrandParentTodayNode = parentNode?.name === todayNodeName;

    if (isGrandParentTodayNode) {
        DisplayToastrError(
            `The '${todayNodeName}' node cannot have grandchildren`,
            "Uh oh"
        );

        return;
    }

    const existingNode = await WithAnimatedProgressBarAsync(() =>
        workflowRepository.DoesExactNodeExistByName(
            ClientDataProvider,
            searchText
        )
    );

    if (existingNode) {
        toastr.error("This node already exists!");
        return;
    }

    Sounds.bellDingDong.Play();

    await WithAnimatedProgressBarAsync(() =>
        SaveNodeAsync(searchText, rootNode.id, canAddNode)
    );
}
